import React, { Suspense } from 'react'
import { Route, Routes, Navigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { moduleApi } from '@/shared/api/moduleApi'
import { queryKeys } from '@/shared/types/queryKeys'
import { getModule } from './ModuleRegistry'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { lazyWithRetry } from '@/shared/utils/lazyWithRetry'

const LearningHubPage = lazyWithRetry(() => import('@/features/learning/LearningHubPage'))
const PlanHubPage = lazyWithRetry(() => import('@/features/planner/PlanHubPage'))
const MorePage = lazyWithRetry(() => import('@/platform/shell/MorePage'))

/**
 * Dynamic route builder — constructs React Router routes from the
 * server module registry at runtime.
 *
 * Flow:
 * 1. Fetch enabled modules from backend (GET /api/platform/modules)
 * 2. For each enabled module, look up its component in the client registry
 * 3. Render a <Route> for each matched module
 * 4. Modules with no client component are silently skipped
 *
 * This component renders inside the AppShell's <Outlet />.
 */
export function ModuleLoader() {
  const location = useLocation()
  const { data: modules = [], isLoading } = useQuery({
    queryKey: queryKeys.modules.all(),
    queryFn: moduleApi.getEnabledModules,
    staleTime: 5 * 60 * 1000,
  })

  if (isLoading) {
    return <ModuleLoadingFallback />
  }

  const defaultModule = modules[0]

  return (
    <Routes location={location}>
      {/* Redirect root to first active module */}
      {defaultModule && (
        <Route
          path="/"
          element={<Navigate to={defaultModule.defaultRoute} replace />}
        />
      )}

      {/* Core Mobile-First 5 Destinations & Aliases */}
      <Route path="/today" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/plan/*"
        element={
          <ErrorBoundary moduleName="Plan">
            <Suspense fallback={<ModuleLoadingFallback />}>
              <PlanHubPage />
            </Suspense>
          </ErrorBoundary>
        }
      />
      <Route
        path="/more/*"
        element={
          <ErrorBoundary moduleName="More">
            <Suspense fallback={<ModuleLoadingFallback />}>
              <MorePage />
            </Suspense>
          </ErrorBoundary>
        }
      />
      <Route path="/settings/*" element={<Navigate to="/more" replace />} />
      <Route path="/profile/*" element={<Navigate to="/more" replace />} />

      {/* Unified Learning Route & Aliases */}
      <Route
        path="/learning/*"
        element={
          <ErrorBoundary moduleName="Learning">
            <Suspense fallback={<ModuleLoadingFallback />}>
              <LearningHubPage />
            </Suspense>
          </ErrorBoundary>
        }
      />
      <Route path="/celpip/format" element={<Navigate to="/learning?tab=format" replace />} />
      <Route path="/language-goal/*" element={<Navigate to="/language-goals" replace />} />

      {/* Render a route for each enabled module that has a client component */}
      {modules.map((module) => {
        const clientModule = getModule(module.id)
        if (!clientModule) return null

        return (
          <Route
            key={module.id}
            path={`${module.defaultRoute}/*`}
            element={
              <ErrorBoundary moduleName={module.name}>
                <Suspense fallback={<ModuleLoadingFallback />}>
                  <clientModule.Component />
                </Suspense>
              </ErrorBoundary>
            }
          />
        )
      })}

      {/* Fallback: show a placeholder if no modules are implemented yet */}
      {modules.length > 0 && modules.every(m => !getModule(m.id)) && (
        <Route path="*" element={<ComingSoonPlaceholder />} />
      )}
    </Routes>
  )
}

function ModuleLoadingFallback() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      minHeight: '200px',
    }}>
      <div style={{
        width: 24,
        height: 24,
        border: '2px solid var(--color-border)',
        borderTopColor: 'var(--color-accent)',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
    </div>
  )
}

function ComingSoonPlaceholder() {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '60vh',
      gap: 'var(--space-4)',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 48 }}>🚧</div>
      <h2 style={{ color: 'var(--color-text-primary)' }}>Coming Soon</h2>
      <p style={{ color: 'var(--color-text-secondary)', maxWidth: 320 }}>
        This module is enabled on the server but not yet implemented.
        Check back soon!
      </p>
    </div>
  )
}
